import { Box, Paper, useTheme, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import i18n from "i18next";
import { useMemo } from "react";
import { CustomerOrderProfitabilityLineKey } from "src/api/generated/erp/sales/customerOrder/profitability/model/customerOrderProfitabilityLineKey.ts";
import { CustomerOrderProfitabilityRowDto } from "src/api/generated/erp/sales/customerOrder/profitability/model/customerOrderProfitabilityRowDto.ts";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AavoECharts } from "src/components/common/echarts/AavoECharts.tsx";
import {
	buildActualCostDistributionData,
	buildCostBreakdownBarChartData,
	buildOverviewBarChartData,
	buildVarianceBarChartData,
	formatProfitabilityComparisonTooltip,
	formatProfitabilityPieTooltip,
	getProfitabilityChartPalette,
	ProfitabilityBarChartDatum,
	ProfitabilityComparisonTooltipParam,
	ProfitabilityPieChartDatum,
	ProfitabilityPieTooltipParam,
	ProfitabilityVarianceChartDatum,
} from "src/components/views/erp/sales/customerOrder/profitability/customerOrderProfitabilityChartData.ts";
import { getCustomerOrderProfitabilityDisplayLabel } from "src/components/views/erp/sales/customerOrder/profitability/customerOrderProfitabilityLineKeyUtils.ts";

interface CustomerOrderProfitabilityChartsProps {
	rowsByKey: Partial<Record<CustomerOrderProfitabilityLineKey, CustomerOrderProfitabilityRowDto>>;
	domesticCurrencyCode: string;
}

export const CustomerOrderProfitabilityCharts = ({ rowsByKey, domesticCurrencyCode }: CustomerOrderProfitabilityChartsProps) => {
	const theme = useTheme();
	const chartPalette = useMemo(() => getProfitabilityChartPalette(theme), [theme]);

	const costBreakdownData = useMemo(() => buildCostBreakdownBarChartData(rowsByKey), [rowsByKey]);
	const actualCostDistributionData = useMemo(() => buildActualCostDistributionData(rowsByKey), [rowsByKey]);
	const overviewData = useMemo(() => buildOverviewBarChartData(rowsByKey), [rowsByKey]);
	const varianceData = useMemo(() => buildVarianceBarChartData(rowsByKey), [rowsByKey]);

	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: {
					xs: "minmax(0, 1fr)",
					md: "repeat(2, minmax(0, 1fr))",
				},
				gridAutoRows: {
					xs: "minmax(380px, auto)",
					md: "minmax(420px, 1fr)",
				},
				gap: 2,
				minWidth: 0,
				minHeight: 0,
				overflow: "hidden",
			}}
		>
			<ChartCard
				title={i18n.t("cost_breakdown")}
				option={createComparisonChartOption(costBreakdownData, chartPalette, domesticCurrencyCode)}
			/>
			<ChartCard
				title={i18n.t("actual_cost_distribution")}
				option={createActualCostDistributionChartOption(
					actualCostDistributionData,
					theme,
					chartPalette,
					domesticCurrencyCode,
				)}
			/>
			<ChartCard
				title={i18n.t("overview")}
				option={createComparisonChartOption(overviewData, chartPalette, domesticCurrencyCode)}
			/>
			<ChartCard
				title={i18n.t("variance_overview")}
				option={createVarianceChartOption(varianceData, chartPalette, domesticCurrencyCode)}
			/>
		</Box>
	);
};

interface ChartCardProps {
	title: string;
	option: object;
}

const ChartCard = ({ title, option }: ChartCardProps) => (
	<Paper
		sx={{
			display: "flex",
			flexDirection: "column",
			minHeight: 0,
			height: "100%",
			overflow: "hidden",
		}}
	>
		<VerticalBox sx={{ flex: 1, minHeight: 0 }}>
			<HorizontalBox
				sx={{
					alignItems: "center",
					gap: 1,
					paddingX: 2,
					paddingY: 1,
					borderBottom: "1px solid",
					borderColor: "divider",
				}}
			>
				<Typography variant="subtitle1">{title}</Typography>
			</HorizontalBox>
			<AavoECharts sx={{ flex: 1, minHeight: 0 }} option={option} />
		</VerticalBox>
	</Paper>
);

const createComparisonChartOption = (
	data: ProfitabilityBarChartDatum[],
	chartPalette: string[],
	domesticCurrencyCode: string,
) => ({
	tooltip: {
		trigger: "axis" as const,
		axisPointer: {
			type: "shadow" as const,
		},
		formatter: (params: Array<ProfitabilityComparisonTooltipParam>) =>
			formatProfitabilityComparisonTooltip(params, domesticCurrencyCode),
	},
	legend: {
		top: 8,
	},
	grid: {
		top: 48,
		left: 12,
		right: 12,
		bottom: 48,
		containLabel: true,
	},
	xAxis: {
		type: "category" as const,
		data: data.map((item) => getCustomerOrderProfitabilityDisplayLabel(item.lineKey)),
		axisLabel: {
			interval: 0,
			rotate: 30,
		},
	},
	yAxis: {
		type: "value" as const,
	},
	series: [
		{
			name: i18n.t("budget"),
			type: "bar",
			color: chartPalette[0],
			data: data.map((item) => item.budget),
		},
		{
			name: i18n.t("forecast"),
			type: "bar",
			color: chartPalette[1],
			data: data.map((item) => item.forecast),
		},
		{
			name: i18n.t("actual"),
			type: "bar",
			color: chartPalette[2],
			data: data.map((item) => item.actual),
		},
	],
});

const createActualCostDistributionChartOption = (
	data: ProfitabilityPieChartDatum[],
	theme: Theme,
	chartPalette: string[],
	domesticCurrencyCode: string,
) => ({
	color: chartPalette,
	tooltip: {
		trigger: "item" as const,
		formatter: (param: ProfitabilityPieTooltipParam) => formatProfitabilityPieTooltip(param, domesticCurrencyCode),
	},
	legend: {
		show: false,
	},
	graphic: data.length === 0 ? {
		type: "text",
		left: "center",
		top: "middle",
		style: {
			text: i18n.t("no_data"),
			fill: theme.palette.text.secondary,
			fontSize: 14,
		},
	} : undefined,
	series: [
		{
			type: "pie",
			radius: "62%",
			center: ["50%", "57%"],
			avoidLabelOverlap: true,
			itemStyle: {
				borderRadius: 4,
				borderColor: theme.palette.background.paper,
				borderWidth: 2,
			},
			label: {
				formatter: "{b} {d}%",
				fontSize: 11,
			},
			labelLine: {
				length: 10,
				length2: 8,
			},
			data: data.map((item) => ({
				name: getCustomerOrderProfitabilityDisplayLabel(item.lineKey),
				value: item.value,
			})),
		},
	],
});

const createVarianceChartOption = (
	data: ProfitabilityVarianceChartDatum[],
	chartPalette: string[],
	domesticCurrencyCode: string,
) => {
	return {
		tooltip: {
			trigger: "axis" as const,
			axisPointer: {
				type: "shadow" as const,
			},
			formatter: (params: Array<ProfitabilityComparisonTooltipParam>) =>
				formatProfitabilityComparisonTooltip(params, domesticCurrencyCode),
		},
		legend: {
			top: 8,
		},
		grid: {
			top: 48,
			left: 12,
			right: 12,
			bottom: 48,
			containLabel: true,
		},
		xAxis: {
			type: "category" as const,
			data: data
				.map((item) => getCustomerOrderProfitabilityDisplayLabel(item.lineKey)),
			axisLabel: {
				interval: 0,
				rotate: 30,
			},
		},
		yAxis: {
			type: "value" as const,
		},
		series: [
			{
				name: i18n.t("actual_vs_budget"),
				type: "bar",
				color: chartPalette[0],
				data: data.map((item) => item.actualVsBudget),
			},
			{
				name: i18n.t("actual_vs_forecast"),
				type: "bar",
				color: chartPalette[1],
				data: data.map((item) => item.actualVsForecast),
			},
		],
	};
};
